import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'quill-mention';
import { Tooltip, Upload } from 'antd';
import { useParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import ReactQuill, { Quill } from 'react-quill';
import { renderToString } from 'react-dom/server';
import CalculatorIcon from '../../../assets/icons/calculator.svg';
import OptionIcon from '../../../assets/icons/option.svg';
import CheckIcon from '../../../assets/icons/check-square.svg';
import NumberIcon from '../../../assets/icons/number.svg';
import DropdownIcon from '../../../assets/icons/dropdown.svg';
import { Parser } from 'expr-eval';
import { filter, map, chain, isEqual, debounce } from 'lodash-es';

import { useDispatch, useSelector } from 'react-redux';

import { Trans, useTranslation } from 'react-i18next';
import 'react-quill/dist/quill.snow.css';

import { alphabetOnlyId } from '../../../utils/tinyid';
import { QuestionCircleOutlined } from '@ant-design/icons';

import { BehaviorSubject, from } from 'rxjs';
import { debounceTime, filter as rxFilter, tap as rxTap } from 'rxjs/operators';

import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

const Clipboard = Quill.import('modules/clipboard');

class PlainClipboard extends Clipboard {
  onPaste(e) {
    e.preventDefault();
    // const range = this.quill.getSelection()
    // const text = e.clipboardData.getData('text/plain')
    // const delta = new Delta()
    // .retain(range.index)
    // .delete(range.length)
    // .insert(text)
    // const index = text.length + range.index
    // const length = 0
    // this.quill.updateContents(delta, 'silent')
    // this.quill.setSelection(index, length, 'silent')
    // this.quill.scrollIntoView()
  }
}

const FORMULA_SUPPORTED_FIELDS = ['CHECKBOX', 'RADIO', 'FORMULA', 'DROPDOWN', 'NUMBER'];
const ICON_MAP = {
  FORMULA: CalculatorIcon,
  CHECKBOX: CheckIcon,
  RADIO: OptionIcon,
  DROPDOWN: DropdownIcon,
  NUMBER: NumberIcon,
};

/**
 * Checks if the pressed key is allowed based on the specified criteria.
 *
 * @param {KeyboardEvent} e - The HTML keypress event.
 * @returns {boolean} - Returns true if the key is allowed, otherwise false.
 */
const checkAllowedChars = (e) => {
  // allow only numbers, operators, parenthesis and @
  const allowedChars = /^[0-9+\-*/^%()@. ]$/;
  if (allowedChars.test(e.key)) return true;

  e.preventDefault();
};

const FormulaEditor = ({ questions, control, getValues, currentQuestion, onChange }) => {
  const { t } = useTranslation(['builder']);
  const [formQuestions] = useState(questions);
  const formulaQuillRef = useRef(null);
  const dispatch = useDispatch();
  const [quillInstanceId, setQuillInstanceId] = useState(0);

  /**
   * Reference to the Tippy instance.
   *
   * @type {React.MutableRefObject<import('tippy.js').Instance[]|null>}
   */
  const tippyRef = useRef(null);
  const mentionOpenRef = useRef(false);
  // const questions = formQuestionsSelector(form);
  // useWhyDidYouUpdate('FormulaEditor', { control, currentQuestion, onChange });

  /**
   * Prevent pasting into the formula editor.
   */
  useEffect(() => {
    Quill.register('modules/clipboard', PlainClipboard, true);
    return () => {
      Quill.register('modules/clipboard', Clipboard, true);
    };
  }, []);

  /**
   * Destroy tippy instances when the component unmounts.
   */
  useEffect(() => {
    return () => {
      if (tippyRef.current) {
        tippyRef.current?.forEach((tippy) => tippy.destroy());
      }
    };
  }, []);

  /**
   * This is needed to prevent a bug that causes Quill instance from being unmounted from the DOM when the `source` func updates due to formQuestions change.
   */
  useEffect(() => {
    setQuillInstanceId((prev) => prev + 1);
  }, [formQuestions]);

  /**
   * Sync current question's formula labels with the new field labels.
   */
  useEffect(() => {
    const { formulaRender = '' } = currentQuestion || {};
    if (!formulaRender) return;
    const html = new DOMParser().parseFromString(formulaRender, 'text/html');
    // update the html content with latest field labels for each field id
    const mentions = html.querySelectorAll('span.mention');
    mentions?.forEach((mention) => {
      const questionId = mention.getAttribute('data-id');
      const newQuestionData = formQuestions.find((q) => q.questionId === questionId);
      if (newQuestionData) {
        mention.setAttribute('data-value', newQuestionData.label);
        /** &#xFEFF; is a zero-width non-breaking space. This added by quill-mention in the original formula and
         * therefor needed here to properly compare the new formula with the old one. */
        mention.innerHTML = `&#xFEFF;<span contenteditable="false">@${newQuestionData.label}</span>&#xFEFF;`;
      }
    });
    const newHtml = html.body.innerHTML;
    if (newHtml !== formulaRender) {
      onChange({ formulaRender: newHtml });
    }
    // eslint-disable-next-line
  }, []); // we only want to run this effect once on mount

  /**
   * Get the valid fields for the formula editor.
   */
  const availableFields = useMemo(() => {
    const fields = formQuestions?.reduce((acc, question) => {
      if (question?.questionId === currentQuestion?.questionId) return acc;
      if (FORMULA_SUPPORTED_FIELDS.includes(question?.type)) {
        if (question?.type === 'FORMULA') {
          const { variables } = question?.formula || {};
          const varIds = variables ? Object.values(variables) : [];
          // prevent cyclic dependency
          if (varIds.includes(currentQuestion?.questionId)) return acc;
        }
        acc.push({
          id: question?.questionId,
          varId: 'VAR_' + alphabetOnlyId(),
          value: question?.label,
          type: question?.type,
        });
      }
      return acc;
    }, []);
    if (fields?.length === 0)
      fields.push({
        id: '0',
        value: t('builder:propertiesPanel.formula.noSupportQuestions'),
        type: 'TEXT',
        disabled: true,
      });
    return fields;
  }, [formQuestions, currentQuestion?.questionId, t]);

  /**
   * The source function for the mention module.
   */
  const source = useCallback(
    (searchTerm, renderList, mentionChar) => {
      let values;

      if (mentionChar === '@') {
        values = availableFields;

        if (searchTerm.length === 0) {
          renderList(values, searchTerm);
        } else {
          const matches = [];
          for (let i = 0; i < values.length; i++)
            if (~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase()))
              matches.push(values[i]);
          renderList(matches, searchTerm);
        }
      }
    },
    [availableFields],
  );

  const renderMentionItem = useCallback((item, searchTerm) => {
    const itemEl = document.createElement('div');
    itemEl.classList.add('mention-item');
    let content;
    if (item?.id === '0') {
      const info = renderToString(
        <QuestionCircleOutlined
          id="no-supported-questions-info"
          style={{ color: 'rgba(0, 0, 0, 0.45)', marginLeft: '4px' }}
        />,
      );
      content = `${item?.value} ${info}`;
    } else {
      const avatar = renderToString(
        <img
          src={ICON_MAP[item?.type]}
          style={{
            width: 20,
            borderRadius: 3,
            padding: 2,
            marginLeft: -3,
            marginBottom: item?.type === 'NUMBER' ? 3 : 0,
          }}
        />,
      );
      content = `${avatar} ${item?.value}`;
    }
    itemEl.innerHTML = content;
    return itemEl;
  }, []);

  // const properties$ = React.useRef(new BehaviorSubject(''));

  // useEffect(() => {
  //   const updateProperties$ = from(properties$.current).pipe(
  //     rxFilter((val) => !!val),
  //     debounceTime(200),
  //     // rxTap((val) => console.log('🚀 ~ updateProperties$', val)),
  //   );

  //   const subscription = updateProperties$.subscribe(() => {
  //     const varIdToQuestionId = {};
  //     const editor = formulaQuillRef.current?.getEditor();
  //     const deltaOps = editor.getContents().ops;

  //     const text = deltaOps
  //       ?.filter(
  //         (op) => typeof op.insert === 'string' || op.insert?.mention instanceof DOMStringMap,
  //       )
  //       .map((op) => {
  //         if (typeof op.insert === 'string') return op.insert;
  //         const mention = op.insert?.mention;
  //         // map varId to questionId
  //         const questionId = mention?.id;
  //         const varId = mention?.varId;
  //         varIdToQuestionId[varId] = questionId;
  //         return varId;
  //       })
  //       .join('')
  //       .trim();
  //     // .value();

  //     dispatch({
  //       type: 'REMOVE_FIELD_ERROR',
  //       payload: { id: currentQuestion?.questionId, errorType: 'invalidFormula' },
  //     });

  //     if (!text) return onChange({ ...getValues(), formula: {} });

  //     try {
  //       const expr = Parser.parse(text);
  //       expr.evaluate(
  //         Object.keys(varIdToQuestionId).reduce((acc, varId) => ((acc[varId] = 1), acc), {}),
  //       );
  //       onChange({
  //         ...getValues(),
  //         formula: {
  //           expression: text,
  //           variables: varIdToQuestionId,
  //         },
  //       });
  //     } catch (e) {
  //       console.log(e?.message);
  //       dispatch({
  //         type: 'SET_FIELD_ERROR',
  //         payload: {
  //           id: currentQuestion?.questionId,
  //           errorType: 'invalidFormula',
  //           errorValue: t('builder:errors.invalidFormula'),
  //         },
  //       });
  //     }
  //   });

  //   return () => {
  //     subscription.unsubscribe();
  //   };
  // }, [dispatch]);

  const onFormulaChange = () => {
    const varIdToQuestionId = {};
    const editor = formulaQuillRef.current?.getEditor();
    const deltaOps = editor.getContents().ops;

    const text = deltaOps
      ?.filter((op) => typeof op.insert === 'string' || op.insert?.mention instanceof DOMStringMap)
      .map((op) => {
        if (typeof op.insert === 'string') return op.insert;
        const mention = op.insert?.mention;
        // map varId to questionId
        const questionId = mention?.id;
        const varId = mention?.varId;
        varIdToQuestionId[varId] = questionId;
        return varId;
      })
      .join('')
      .trim();
    // .value();

    dispatch({
      type: 'REMOVE_FIELD_ERROR',
      payload: { id: currentQuestion?.questionId, errorType: 'invalidFormula' },
    });

    if (!text) return onChange({ ...getValues(), formula: {} });

    try {
      const expr = Parser.parse(text);
      expr.evaluate(
        Object.keys(varIdToQuestionId).reduce((acc, varId) => ((acc[varId] = 1), acc), {}),
      );

      onChange({
        ...getValues(),
        formula: {
          expression: text,
          variables: varIdToQuestionId,
        },
      });
    } catch (e) {
      console.log(e?.message);
      if (mentionOpenRef.current) return;
      dispatch({
        type: 'SET_FIELD_ERROR',
        payload: {
          id: currentQuestion?.questionId,
          errorType: 'invalidFormula',
          errorValue: t('builder:errors.invalidFormula'),
        },
      });
    }
  };
  const debouncedOnFormulaChange = useCallback(debounce(onFormulaChange, 200), []);

  // const onMentionSelect = useCallback((item, insertItem) => {
  //   const { id } = item;
  //   if (id === '0') return;

  //   // // clear error if no cyclic dependency
  //   // dispatch({
  //   //   type: 'REMOVE_FIELD_ERROR',
  //   //   payload: { id: currentQuestion?.questionId, errorType: 'mutallyDependant' },
  //   // });

  //   // // check for cyclic dependency by checking if current formula is already used in the item's formula
  //   // const currentFormulaId = currentQuestion?.questionId;
  //   // const itemQuestion = formQuestions.find((q) => q.questionId === id && q.type === 'FORMULA');
  //   // if (itemQuestion) {
  //   //   const { formula } = itemQuestion;
  //   //   const { variables } = formula;
  //   //   const varIds = variables ? Object.values(variables) : [];

  //   //   if (varIds.includes(currentFormulaId)) {
  //   //     dispatch({
  //   //       type: 'SET_FIELD_ERROR',
  //   //       payload: {
  //   //         id: currentQuestion?.questionId,
  //   //         errorType: 'mutallyDependant',
  //   //         errorValue: (
  //   //           <Trans
  //   //             i18nKey="builder:errors.mutallyDependant"
  //   //             values={{ question: itemQuestion?.label }}
  //   //           />
  //   //         ),
  //   //       },
  //   //     });
  //   //   }
  //   // }

  //   insertItem(item);
  //   // properties$.current.next('update');
  // }, []);

  const onMentionOpen = useCallback(() => {
    mentionOpenRef.current = true;
    tippyRef.current = tippy('#no-supported-questions-info', {
      content: t('builder:propertiesPanel.formula.noSupportQuestionsInfo'),
      arrow: true,
      animation: 'scale',
      maxWidth: 300,
    });
  }, [t]);

  const onMentionClose = useCallback(() => {
    mentionOpenRef.current = false;
    if (tippyRef.current) {
      tippyRef.current?.forEach((tippy) => tippy.destroy());
    }
  }, []);

  return (
    <div className="form-group">
      <label className="form-group-label">
        {t('builder:propertiesPanel.formula.label', 'Calculation')}
        <Tooltip
          placement="top"
          title={t(
            'builder:propertiesPanel.formula.labelTooltip',
            'Insert a formula to calculate the value of this field',
          )}
        >
          <QuestionCircleOutlined />
        </Tooltip>
      </label>
      <Controller
        render={({ field }) => (
          <div>
            <EditorToolBar quillRef={formulaQuillRef} />
            <ReactQuill
              key={quillInstanceId}
              bounds=".properties-container"
              // formats={['bold', 'italic', 'mention']}
              id="formula-editor"
              modules={{
                toolbar: {
                  container: '#ql-custom-toolbar',
                },
                keyboard: {
                  bindings: {
                    tab: false,
                  },
                },
                mention: {
                  allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
                  mentionDenotationChars: ['@'],
                  spaceAfterInsert: false,
                  dataAttributes: ['id', 'value', 'varId'],
                  source,
                  renderItem: renderMentionItem,
                  // onSelect: onMentionSelect,
                  onOpen: onMentionOpen,
                  onClose: onMentionClose,
                  positioningStrategy: 'fixed',
                },
              }}
              {...field}
              ref={(e) => {
                field.ref(e);
                formulaQuillRef.current = e;
              }}
              onPaste={(e) => {
                e.preventDefault();
              }}
              onKeyPress={checkAllowedChars}
              onChange={(value, _, __, editor) => {
                field.onChange(value);
                if (value === '<p><br></p>') {
                  // set editor cursor at the end
                  const quillEditor = formulaQuillRef.current?.getEditor();
                  setTimeout(() => quillEditor.setSelection(editor.getLength() + 1, 0), 0);
                }
                debouncedOnFormulaChange();
                // properties$.current.next('update');
              }}
            />
          </div>
        )}
        name="formulaRender"
        control={control}
      />
      {/* save button */}
      {/* <Button type="primary" size="small" style={{ alignSelf: 'flex-end' }}>
        {t('builder:propertiesPanel.formula.save', 'Save')}
      </Button> */}
    </div>
  );
};

const EditorToolBar = forwardRef(function EditorToolBar(props, ref) {
  const { quillRef } = props;

  const insertChar = useCallback(
    (char) => (e) => {
      const editor = quillRef.current?.getEditor();
      const range = editor.getSelection(true);
      editor.insertText(range.index, char);
      editor.setSelection(range.index + 1, 0);
    },
    [quillRef],
  );

  return (
    <div id="ql-custom-toolbar">
      <span className="ql-formats">
        <button
          tabIndex={-1}
          onClick={() => {
            // insert a @ sign
            const editor = quillRef.current?.getEditor();
            const range = editor.getSelection(true);
            editor.setSelection(range.index + 1, 0);
            editor.getModule('mention').openMenu('@');
          }}
          title="Insert a question"
        >
          {/* <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5H11V19H8V21H16V19H13V5H16V3H8V5ZM2 7C1.44772 7 1 7.44772 1 8V16C1 16.5523 1.44772 17 2 17H8V15H3V9H8V7H2ZM16 9H21V15H16V17H22C22.5523 17 23 16.5523 23 16V8C23 7.44772 22.5523 7 22 7H16V9Z"></path>
          </svg> */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18 15L17.999 18H21V20H17.999L18 23H16L15.999 20H13V18H15.999L16 15H18ZM11 18V20H3V18H11ZM21 11V13H3V11H21ZM21 4V6H3V4H21Z"></path>
          </svg>
        </button>
      </span>
      <span className="ql-formats">
        <button tabIndex={-1} onClick={insertChar('+')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11 11V5H13V11H19V13H13V19H11V13H5V11H11Z"></path>
          </svg>
        </button>
        <button tabIndex={-1} onClick={insertChar('-')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 11V13H19V11H5Z"></path>
          </svg>
        </button>
        <button tabIndex={-1} onClick={insertChar('*')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.9997 10.5865L16.9495 5.63672L18.3637 7.05093L13.4139 12.0007L18.3637 16.9504L16.9495 18.3646L11.9997 13.4149L7.04996 18.3646L5.63574 16.9504L10.5855 12.0007L5.63574 7.05093L7.04996 5.63672L11.9997 10.5865Z"></path>
          </svg>
        </button>
        <button tabIndex={-1} onClick={insertChar('/')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 11H19V13H5V11ZM12 8C11.1716 8 10.5 7.32843 10.5 6.5C10.5 5.67157 11.1716 5 12 5C12.8284 5 13.5 5.67157 13.5 6.5C13.5 7.32843 12.8284 8 12 8ZM12 19C11.1716 19 10.5 18.3284 10.5 17.5C10.5 16.6716 11.1716 16 12 16C12.8284 16 13.5 16.6716 13.5 17.5C13.5 18.3284 12.8284 19 12 19Z"></path>
          </svg>
        </button>
        <button
          tabIndex={-1}
          onClick={() => {
            // insert a plus sign
            const editor = quillRef.current?.getEditor();
            const range = editor.getSelection(true);
            editor.insertText(range.index, '(');
            editor.insertText(range.index + range.length + 1, ')');
            editor.setSelection(range.index + 1, 0);
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6.92316 21C5.11268 18.6642 4 15.493 4 12C4 8.50702 5.11268 5.33579 6.92316 3H8.93729C7.23506 5.3884 6.2 8.54241 6.2 12C6.2 15.4576 7.23506 18.6116 8.93729 21H6.92316ZM17.0741 21H15.06C16.7622 18.6116 17.7973 15.4576 17.7973 12C17.7973 8.54241 16.7622 5.3884 15.06 3H17.0741C18.8846 5.33579 19.9973 8.50702 19.9973 12C19.9973 15.493 18.8846 18.6642 17.0741 21Z"></path>
          </svg>
        </button>
      </span>
    </div>
  );
});

const MemoizedComponent = memo(FormulaEditor, (prevProps, nextProps) => false);
export default MemoizedComponent;
